import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Snackbar from '@mui/material/Snackbar'
import Typography from '@mui/material/Typography'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import {useLocation} from 'react-router-dom'
import {useFeatureFlag, usePostHog} from '@reachinsight-app/shared/src'
import {useAuth} from '../hooks/useAuth'
import {config} from '../config/environment'
import {SERIF_HEADING} from '../theme'
import {DevLoginPicker} from '../components/DevLoginPicker'
import {EmailCodeLogin} from '../components/EmailCodeLogin'
import {RosFeatureContext} from './useRosFeatures'
import {
  allRosUiFeaturesDisabled,
  parseRosUiFeatureSnapshot,
  type RosAuthFetchOptions,
  type RosFeatureRefreshReason,
  type RosFeatureState,
  type RosUiFeatureSnapshot,
} from './types'

const REFRESH_AGE_MS = 30_000

interface FeatureRecord {
  ownerId: string | null
  receivedAt: number
  state: RosFeatureState
}

export function RosFeatureProvider({children}: {children: React.ReactNode}) {
  const auth = useAuth()
  const location = useLocation()
  // Bootstrap-only presentation flag. Authenticated grants below never use browser PostHog.
  const oauthEnabled = useFeatureFlag('ros-oauth-enabled')
  const {identifyUser} = usePostHog()
  const [record, setRecord] = useState<FeatureRecord>({ownerId: null, receivedAt: 0, state: {status: 'loading', snapshot: null}})
  const [noticeOpen, setNoticeOpen] = useState(false)
  const recordRef = useRef(record)
  const authRef = useRef(auth)
  const inFlightRef = useRef<{ownerId: string; promise: Promise<void>} | null>(null)
  recordRef.current = record
  authRef.current = auth

  useEffect(() => {
    if (auth.ready && auth.email) identifyUser(auth.email)
  }, [auth.ready, auth.email, identifyUser])

  const refreshFeatures = useCallback((reason: RosFeatureRefreshReason = 'manual'): Promise<void> => {
    const currentAuth = authRef.current
    const ownerId = currentAuth.userId
    if (!currentAuth.ready || currentAuth.error || !ownerId) return Promise.resolve()
    if (inFlightRef.current?.ownerId === ownerId) return inFlightRef.current.promise

    if (recordRef.current.ownerId !== ownerId) {
      setRecord({ownerId, receivedAt: 0, state: {status: 'loading', snapshot: null}})
    }

    const promise = (async () => {
      const request = async (token: string | null): Promise<Response> => {
        const headers = new Headers()
        if (token) headers.set('Authorization', `Bearer ${token}`)
        return fetch(`${config.API_CF_URL}/api/r3/features`, {credentials: 'include', cache: 'no-store', headers})
      }
      try {
        let response = await request(currentAuth.token)
        if (response.status === 401) {
          const freshToken = await currentAuth.refresh()
          if (freshToken) response = await request(freshToken)
        }
        if (!response.ok) {
          const status = response.status === 401 || response.status === 403 ? 'denied' : 'error'
          setRecord({ownerId, receivedAt: Date.now(), state: {status, snapshot: allRosUiFeaturesDisabled()}})
          return
        }
        const snapshot = parseRosUiFeatureSnapshot(await response.json())
        if (!snapshot) throw new Error('Invalid Ros feature response')
        // Ignore an old principal's request if the account changed while it was in flight.
        if (authRef.current.userId !== ownerId) return
        setRecord({ownerId, receivedAt: Date.now(), state: {status: 'ready', snapshot}})
      } catch {
        if (authRef.current.userId !== ownerId) return
        setRecord({ownerId, receivedAt: Date.now(), state: {status: 'error', snapshot: allRosUiFeaturesDisabled()}})
      } finally {
        if (inFlightRef.current?.ownerId === ownerId) inFlightRef.current = null
      }
      void reason // reserved for refresh telemetry without exposing snapshot contents
    })()
    inFlightRef.current = {ownerId, promise}
    return promise
  }, [])

  const lastTokenRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (!auth.ready || auth.error || !auth.userId) return
    const ownerChanged = recordRef.current.ownerId !== auth.userId
    const tokenChanged = lastTokenRef.current !== undefined && lastTokenRef.current !== auth.token
    lastTokenRef.current = auth.token
    void refreshFeatures(ownerChanged ? 'initial' : tokenChanged ? 'token_refresh' : 'initial')
  }, [auth.ready, auth.error, auth.userId, auth.token, refreshFeatures])

  useEffect(() => {
    const refreshIfStale = () => {
      if (document.visibilityState !== 'visible') return
      const current = recordRef.current
      if (Date.now() - current.receivedAt > REFRESH_AGE_MS) void refreshFeatures('focus')
    }
    document.addEventListener('visibilitychange', refreshIfStale)
    window.addEventListener('focus', refreshIfStale)
    return () => {
      document.removeEventListener('visibilitychange', refreshIfStale)
      window.removeEventListener('focus', refreshIfStale)
    }
  }, [refreshFeatures])

  const reportUnavailable = useCallback(() => setNoticeOpen(true), [])
  const authFetch = useCallback(async (input: string, init: RequestInit = {}, options: RosAuthFetchOptions = {}): Promise<Response> => {
    const withToken = (token: string | null): RequestInit => {
      const headers = new Headers(init.headers)
      if (token) headers.set('Authorization', `Bearer ${token}`)
      else headers.delete('Authorization')
      return {credentials: 'include', ...init, headers}
    }
    const currentAuth = authRef.current
    let response = await fetch(input, withToken(currentAuth.token))
    if (response.status === 401) {
      const freshToken = await currentAuth.refresh()
      if (freshToken) response = await fetch(input, withToken(freshToken))
    }
    if (response.status === 403) {
      if (!options.silentForbidden) reportUnavailable()
      void refreshFeatures('forbidden')
    }
    return response
  }, [refreshFeatures, reportUnavailable])

  const effectiveState: RosFeatureState = record.ownerId === auth.userId
    ? record.state
    : {status: 'loading', snapshot: null}
  const snapshot: RosUiFeatureSnapshot = effectiveState.snapshot ?? allRosUiFeaturesDisabled()
  const contextValue = useMemo(() => ({
    state: effectiveState,
    snapshot,
    auth,
    enabled: (key: keyof RosUiFeatureSnapshot['flags']) => effectiveState.status === 'ready' && snapshot.flags[key] === true,
    internalGeneralPurpose: effectiveState.status === 'ready'
      && snapshot.userType === 'internal' && snapshot.flags['ros-chat-general-purpose'] === true,
    refreshFeatures,
    authFetch,
    reportUnavailable,
  }), [effectiveState, snapshot, auth, refreshFeatures, authFetch, reportUnavailable])

  if (!auth.ready) return <FullScreenLoader />
  if (config.EMAIL_CODE_AUTH_ENABLED && !auth.token) {
    return <EmailCodeLogin message={auth.error} />
  }
  if (auth.needsLogin) return <DevLoginPicker />
  if (auth.error) {
    const returnHere = encodeURIComponent(location.pathname + location.search)
    return <AccessMessage
      title="Welcome to Ros"
      body={auth.error}
      actionLabel="Sign in"
      actionHref={oauthEnabled ? undefined : `${config.STUDIO_URL}/chat?return=${returnHere}`}
      onAction={oauthEnabled ? auth.signIn : undefined}
    />
  }
  if (effectiveState.status === 'loading') return <FullScreenLoader />
  if (effectiveState.status === 'error') {
    return <AccessMessage title="Ros is temporarily unavailable" body="We couldn't verify your access. Try again in a moment." actionLabel="Retry" onAction={() => void refreshFeatures('manual')} />
  }
  if (effectiveState.status === 'denied') {
    return <AccessMessage title="Your session needs attention" body="We couldn't verify your session or access." actionLabel="Retry" onAction={() => void refreshFeatures('manual')} />
  }
  if (!snapshot.flags['reachy-enabled']) {
    return <AccessMessage title="Ros Not Available" body="Ros is not currently enabled for your account. Please contact your administrator or support team to request access." />
  }

  return (
    <RosFeatureContext.Provider value={contextValue}>
      {children}
      <Snackbar open={noticeOpen} autoHideDuration={4000} onClose={() => setNoticeOpen(false)} message="That feature is not available for your account." />
    </RosFeatureContext.Provider>
  )
}

export function FullScreenLoader() {
  return <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', bgcolor: 'background.default'}}><CircularProgress sx={{color: 'primary.main'}} /></Box>
}

function AccessMessage({title, body, actionLabel, actionHref, onAction}: {
  title: string
  body: string
  actionLabel?: string
  actionHref?: string
  onAction?: () => void
}) {
  return (
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3, bgcolor: 'background.default'}}>
      <Paper elevation={0} sx={{p: 4, maxWidth: 500, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 2}}>
        <LockOutlinedIcon sx={{fontSize: 56, color: 'text.secondary', mb: 2}} />
        <Typography variant="h5" sx={{...SERIF_HEADING, mb: 1}}>{title}</Typography>
        <Typography color="text.secondary" sx={{mb: actionLabel ? 3 : 0}}>{body}</Typography>
        {actionLabel && <Button variant="contained" fullWidth href={actionHref} onClick={onAction}>{actionLabel}</Button>}
      </Paper>
    </Box>
  )
}
